import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdminLayout from '../../layouts/AdminLayout';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import { listExams, deleteExam, togglePublish, cloneExam } from '../../api/admin.api';

const StatusBadge = ({ published }) => (
  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
    published ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
  }`}>
    <span className={`w-1.5 h-1.5 rounded-full ${published ? 'bg-emerald-500' : 'bg-slate-400'}`} />
    {published ? 'Published' : 'Draft'}
  </span>
);

const ExamList = () => {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    listExams()
      .then((res) => setExams(res.data.data))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this exam? This cannot be undone.')) return;
    await deleteExam(id);
    load();
  };

  const handleToggle = async (id) => {
    await togglePublish(id);
    load();
  };

  const handleClone = async (id) => {
    await cloneExam(id);
    load();
  };

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-7">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Exams</h1>
          <p className="text-slate-500 text-sm mt-0.5">{exams.length} exam{exams.length !== 1 ? 's' : ''} total</p>
        </div>
        <Button onClick={() => navigate('/admin/exams/new')} size="md">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create Exam
        </Button>
      </div>

      {loading ? (
        <Spinner className="py-20" />
      ) : exams.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center text-3xl mx-auto mb-4">📝</div>
          <h3 className="text-slate-700 font-semibold mb-1">No exams yet</h3>
          <p className="text-slate-400 text-sm mb-5">Create your first exam to get started.</p>
          <Button onClick={() => navigate('/admin/exams/new')}>Create Exam</Button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Title</th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Duration</th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Questions</th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {exams.map((exam) => (
                <tr key={exam.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-5 py-4">
                    <Link
                      to={`/admin/exams/${exam.id}`}
                      className="font-semibold text-slate-800 hover:text-primary-600 transition-colors"
                    >
                      {exam.title}
                    </Link>
                    {exam.description && (
                      <p className="text-slate-400 text-xs mt-0.5 truncate max-w-xs">{exam.description}</p>
                    )}
                  </td>
                  <td className="px-5 py-4 text-slate-500">
                    <span className="inline-flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {exam.duration_minutes} min
                    </span>
                  </td>
                  <td className="px-5 py-4 text-slate-500">{exam.question_count ?? 0}</td>
                  <td className="px-5 py-4">
                    <StatusBadge published={exam.is_published} />
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1.5">
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/admin/exams/${exam.id}`)}>
                        View
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/admin/exams/${exam.id}/edit`)}>
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant={exam.is_published ? 'secondary' : 'success'}
                        onClick={() => handleToggle(exam.id)}
                      >
                        {exam.is_published ? 'Unpublish' : 'Publish'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleClone(exam.id)}>
                        Clone
                      </Button>
                      <Button size="sm" variant="danger" onClick={() => handleDelete(exam.id)}>
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
};

export default ExamList;
